import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SearchResult } from '@/types/search';
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch';
import type { StreamRef } from '@/types/streaming';

interface Props {
  result: SearchResult;
}

function yearOf(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4})/.exec(iso);
  return m ? m[1] : null;
}

function ratingOf(v: number | null): string | null {
  if (v === null || v === 0) return null;
  return v.toFixed(1);
}

function watchPathFor(result: SearchResult): string {
  if (result.mediaType === 'movie') return `/watch/movie/${result.id}`;
  if (result.mediaType === 'anime') return `/watch/anime/${result.id}/1/${result.defaultAudio}`;
  return `/watch/tv/${result.id}/1/1`;
}

function streamRefFor(result: SearchResult): StreamRef {
  if (result.mediaType === 'movie') return { type: 'movie', tmdbId: result.id };
  if (result.mediaType === 'anime') {
    return { type: 'anime', malId: result.id, episode: 1, audio: result.defaultAudio };
  }
  return { type: 'tv', tmdbId: result.id, season: 1, episode: 1 };
}

function chipKeyFor(result: SearchResult): 'chipMovie' | 'chipAnime' | 'chipSeries' {
  if (result.mediaType === 'movie') return 'chipMovie';
  if (result.mediaType === 'anime') return 'chipAnime';
  return 'chipSeries';
}

export function ResultCard({ result }: Props): JSX.Element {
  const { t } = useTranslation();
  const year = yearOf(result.releaseDate);
  const rating = ratingOf(result.voteAverage);
  const chip = t(`search.${chipKeyFor(result)}`);
  const prefetchHandlers = useHoverPrefetch(streamRefFor(result));

  return (
    <Link
      to={watchPathFor(result)}
      className="result-card"
      aria-label={t('search.cardAria', { title: result.title, chip })}
      {...prefetchHandlers}
    >
      <div className="result-card__poster">
        {result.posterUrl ? (
          <img src={result.posterUrl} alt={t('search.posterAlt', { title: result.title })} loading="lazy" />
        ) : (
          <div className="result-card__poster--empty">{t('search.noPoster')}</div>
        )}
        <span className="result-card__chip">{chip}</span>
        {rating && <span className="result-card__rating">★ {rating}</span>}
      </div>
      <div className="result-card__title">{result.title}</div>
      {year && <div className="result-card__year">{year}</div>}
    </Link>
  );
}
